import React, { useCallback, useEffect, useState } from "react"
import { useFetcher } from "react-router"

import { fetchAPI, handleResponse } from "@/utils"
import type { EnhancedConfigData } from "./brand-lenses-configuration"
import { SharedTimelineTab, type TimelineConfig } from "../shared/SharedTimelineTab"
import type { TimelineConfig as StoredTimelineConfig } from "@/utils/types"
interface FetcherData {
  success?: boolean
  action?: string
  errorMessage?: string
}

export function TimelineTab({ data, config }: { data: EnhancedConfigData; config: StoredTimelineConfig | undefined }) {
  const fetcher = useFetcher<FetcherData>()

  const [evidenceCountLabels, setEvidenceCountLabels] = useState<string[]>([])
  const [isLoadingLabels, setIsLoadingLabels] = useState(false)

  const [timelineConfig, setTimelineConfig] = useState<TimelineConfig>(() => {
    const defaultConfig = getDefaultTimelineConfig()

    if (!config) {
      return defaultConfig
    }
    return {
      general: config.general ?? defaultConfig.general,
      upper: {
        allowManualPeriodDateAdjustment: config.upper?.allowManualPeriodDateAdjustment ?? false,
        allowManualPeriodOptionsAdjustment: config.upper?.allowManualPeriodOptionsAdjustment ?? false,
        periodNameSingular: config.upper?.periodNameSingular ?? "",
        periodNamePlural: config.upper?.periodNamePlural ?? "",
        periodOverlapMonths: config.upper?.periodOverlapMonths ?? 0,
        periodOverlapMonthsToEOM: config.upper?.periodOverlapMonthsToEOM ?? false,
        disableOverlapMonths: config.upper?.disableOverlapMonths ?? false,
        periodOptions: config.upper?.periodOptions ?? [],
        icons: {
          autoTag: config.upper?.icons?.autoTag ?? {},
          userTag: config.upper?.icons?.userTag ?? {},
          evidenceCounts: config.upper?.icons?.evidenceCounts ?? [],
        },
      },
      lower: config.lower ?? defaultConfig.lower,
      instances: {
        showSequenceNumber: config.instances?.showSequenceNumber ?? false,
        instanceStyle: {
          defaultBackground: config.instances?.instanceStyle?.defaultBackground ?? "#ffffff",
          defaultColour: config.instances?.instanceStyle?.defaultColour ?? "#000000",
          type: config.instances?.instanceStyle?.type ?? "",
          instanceTag: config.instances?.instanceStyle?.instanceTag ?? "",
          values: config.instances?.instanceStyle?.values ?? [],
        },
        instanceIcons: config.instances?.instanceIcons ?? [],
      },
    }
  })

  const pendingAction = (fetcher.json as { action?: string } | undefined)?.action ?? null

  useEffect(() => {
    setIsLoadingLabels(true)
    fetchAPI<string[]>(`/api/unityviewer/evidencecount/labels/${data.loadedOrganisation}/${data.loadedProject}/record`)
      .then(handleResponse)
      .then((labels) => {
        setEvidenceCountLabels(labels ?? [])
      })
      .catch((err) => {
        console.error("Failed to load evidence count labels:", err)
        setEvidenceCountLabels([])
      })
      .finally(() => {
        setIsLoadingLabels(false)
      })
  }, [data.loadedOrganisation, data.loadedProject])

  const handleSave = useCallback(
    (newConfig: TimelineConfig) => {
      const submitData: Record<string, any> = {
        action: "SAVE_SECTION",
        organisation: data.loadedOrganisation,
        project: data.loadedProject,
        jsonPath: "$.timeline",
        value: newConfig,
        concurrency: data.existingConfig?.concurrency ?? "",
      }

      fetcher.submit(submitData, {
        method: "post",
        encType: "application/json",
      })
    },
    [fetcher, data],
  )

  const handleRemove = useCallback(() => {
    const submitData = {
      action: "REMOVE_SECTION",
      organisation: data.loadedOrganisation,
      project: data.loadedProject,
      jsonPath: "$.timeline",
      concurrency: data.existingConfig?.concurrency ?? "",
    }

    fetcher.submit(submitData, {
      method: "post",
      encType: "application/json",
    })
  }, [fetcher, data])

  return (
    <SharedTimelineTab
      timelineConfig={timelineConfig}
      onConfigChange={setTimelineConfig}
      onSave={handleSave}
      onRemove={handleRemove}
      isFetching={fetcher.state !== "idle"}
      currentAction={pendingAction as "SAVE_SECTION" | "REMOVE_SECTION" | null}
      evidenceCountLabels={evidenceCountLabels}
      isLoadingLabels={isLoadingLabels}
    />
  )
}

function getDefaultTimelineConfig(): TimelineConfig {
  return {
    general: { useUpper: false, useLower: false, useInstances: false },
    upper: {
      allowManualPeriodDateAdjustment: false,
      allowManualPeriodOptionsAdjustment: false,
      periodNameSingular: "",
      periodNamePlural: "",
      periodOverlapMonths: 0,
      periodOverlapMonthsToEOM: false,
      disableOverlapMonths: false,
      periodOptions: [],
      icons: {
        autoTag: {},
        userTag: {},
        evidenceCounts: [],
      },
    },
    lower: { showRecordClusters: false },
    instances: {
      showSequenceNumber: false,
      instanceStyle: {
        defaultBackground: "#ffffff",
        defaultColour: "#000000",
        type: "",
        instanceTag: "",
        values: [],
      },
      instanceIcons: [],
    },
  }
}
